// Client Portal — Distribution add-on (social publishing broker).
// Connected Accounts management + monthly usage + post calendar. Publishing
// itself is driven by Hyve/n8n; this portal owns the human parts:
// connect/disconnect, visibility into limits and platform approval states,
// and calendar-level reschedule/cancel.
// ⚠️ All user-facing copy in this file is draft — flagged for human review
// (Hard Rule 8) before client exposure.
import { useCallback, useEffect, useState } from "react";
import { Share2, Lock, RefreshCw, Unplug } from "lucide-react";
import { distributionFetch } from "../lib/api";
import { DistributionCalendar, PLATFORM_LABELS } from "./DistributionCalendar";
import { DistributionAnalytics } from "./DistributionAnalytics";

const STATUS_BADGES = {
  active: "bg-green-100 text-green-700",
  expired: "bg-amber-100 text-amber-700",
  revoked: "bg-gray-100 text-gray-500",
  error: "bg-red-100 text-red-700",
};

export function DistributionPortal({ facility, user }) {
  const [tab, setTab] = useState("accounts");
  const [connections, setConnections] = useState(null);
  const [usage, setUsage] = useState(null);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyPlatform, setBusyPlatform] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [conns, use] = await Promise.all([
        distributionFetch(`/connections/${facility.facility_id}`),
        distributionFetch(`/usage/${facility.facility_id}`),
      ]);
      setConnections(conns);
      setUsage(use);
      setLocked(!use.entitled);
    } catch (e) {
      if (e.detail?.status === "not_entitled") setLocked(true);
      else setError("Could not load distribution status. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [facility.facility_id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleConnect = async (platform) => {
    setBusyPlatform(platform);
    setError(null);
    try {
      const res = await distributionFetch("/connect/start", {
        method: "POST",
        body: JSON.stringify({
          facility_id: facility.facility_id,
          platform,
          connected_by: user?.id || user?.email || "",
        }),
      });
      window.open(res.authorize_url, "_blank", "noopener");
    } catch (e) {
      if (e.detail?.status === "pending_approval") {
        setError(
          `${PLATFORM_LABELS[platform]?.label || platform} is pending platform approval and can't be connected yet.`,
        );
      } else if (e.detail?.status === "platform_not_in_plan") {
        setError(
          `${PLATFORM_LABELS[platform]?.label || platform} isn't included in your plan.`,
        );
      } else {
        setError("Could not start the connection. Please try again.");
      }
    } finally {
      setBusyPlatform(null);
    }
  };

  const handleDisconnect = async (accountId, name) => {
    if (
      !window.confirm(
        `Disconnect ${name || "this account"}? Stored access tokens are deleted immediately and scheduled posts to it will fail.`,
      )
    )
      return;
    try {
      await distributionFetch(
        `/connections/${facility.facility_id}/accounts/${accountId}`,
        { method: "DELETE" },
      );
      await load();
    } catch {
      setError("Disconnect failed. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-gray-500 flex items-center gap-2">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading distribution…
      </div>
    );
  }

  if (locked) {
    // Visible-but-locked until purchased (punch list Phase 5).
    return (
      <div className="p-8 max-w-2xl">
        <div className="border rounded-xl p-8 text-center bg-gray-50">
          <Lock className="w-10 h-10 mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Distribution</h2>
          <p className="text-gray-600 mb-4">
            Publish your Hyve-generated content straight to Facebook, Instagram,
            YouTube, TikTok, LinkedIn, and X — and feed engagement data back
            into what gets generated next.
          </p>
          <p className="text-sm text-gray-500">
            The Distribution add-on isn't active for this facility. Contact your
            Hyve administrator to enable it.
          </p>
        </div>
      </div>
    );
  }

  const platforms = connections?.platforms || [];

  return (
    <div className="p-8 space-y-8 max-w-5xl">
      <div className="flex flex-wrap items-center gap-3">
        <Share2 className="w-6 h-6 text-sky-600" />
        <h2 className="text-xl font-semibold">Distribution</h2>
        <div className="ml-auto flex rounded-lg border overflow-hidden text-sm">
          {[
            ["accounts", "Connected Accounts"],
            ["calendar", "Calendar"],
            ["analytics", "Analytics"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-3 py-1.5 ${tab === key ? "bg-sky-600 text-white" : "hover:bg-gray-50"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === "calendar" && <DistributionCalendar facility={facility} />}

      {tab === "analytics" && <DistributionAnalytics facility={facility} />}

      {tab === "accounts" && (
        <>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
              {error}
            </div>
          )}

          {usage && (
            <div className="border rounded-xl p-4 bg-white">
              <div className="text-sm font-medium text-gray-700 mb-2">
                This month
              </div>
              <div className="flex flex-wrap gap-6 text-sm">
                <div>
                  <span className="font-semibold">{usage.posts_used}</span>
                  {usage.posts_per_month > 0 && (
                    <span className="text-gray-500">
                      {" "}
                      / {usage.posts_per_month}
                    </span>
                  )}{" "}
                  posts
                  {usage.posts_remaining !== null && (
                    <span className="text-gray-400">
                      {" "}
                      ({usage.posts_remaining} remaining)
                    </span>
                  )}
                </div>
                {usage.x_enabled && usage.platforms?.x && (
                  <div className="text-gray-600">
                    X usage cost est.{" "}
                    <span className="font-semibold">
                      ${usage.platforms.x.estimated_cost_usd?.toFixed(2)}
                    </span>{" "}
                    ({usage.platforms.x.posts} posts,{" "}
                    {usage.platforms.x.link_posts} with links)
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            {platforms.map((p) => {
              const meta = PLATFORM_LABELS[p.platform] || { label: p.platform };
              const inPlan = usage?.allowed_platforms?.includes(p.platform);
              const monthly = usage?.platforms?.[p.platform];
              return (
                <div
                  key={p.platform}
                  className="border rounded-xl p-4 bg-white"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium">
                      <span className="mr-2">{meta.icon}</span>
                      {meta.label}
                    </div>
                    {!p.available ? (
                      <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700">
                        Pending platform approval
                      </span>
                    ) : !inPlan ? (
                      <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-500">
                        Not in plan
                      </span>
                    ) : (
                      <button
                        onClick={() => handleConnect(p.platform)}
                        disabled={busyPlatform === p.platform}
                        className="text-xs px-3 py-1 rounded bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
                      >
                        {p.accounts.length ? "Reconnect" : "Connect"}
                      </button>
                    )}
                  </div>
                  {monthly?.posts > 0 && (
                    <div className="text-xs text-gray-400 mb-2">
                      {monthly.posts} post{monthly.posts === 1 ? "" : "s"} this
                      month
                    </div>
                  )}
                  {p.accounts.length === 0 ? (
                    <div className="text-sm text-gray-400">Not connected</div>
                  ) : (
                    <ul className="space-y-2">
                      {p.accounts.map((a) => (
                        <li
                          key={a.account_id}
                          className="flex items-center justify-between text-sm"
                        >
                          <div>
                            <span className="font-medium">
                              {a.name || a.external_account_id}
                            </span>
                            <span
                              className={`ml-2 text-xs px-2 py-0.5 rounded ${STATUS_BADGES[a.status] || ""}`}
                              title={a.status_detail || ""}
                            >
                              {a.status}
                            </span>
                          </div>
                          {a.status !== "revoked" && (
                            <button
                              onClick={() =>
                                handleDisconnect(a.account_id, a.name)
                              }
                              className="text-gray-400 hover:text-red-600"
                              title="Disconnect — deletes stored tokens immediately"
                            >
                              <Unplug className="w-4 h-4" />
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-xs text-gray-400">
            Connecting opens the platform's own consent screen in a new tab.
            Hyve requests only the permissions listed in our{" "}
            <a
              href="https://hyveappliedintelligence.com/privacy.html"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Privacy Policy
            </a>
            . Disconnecting deletes stored access tokens immediately.
          </p>
        </>
      )}
    </div>
  );
}
